import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

import { Download, DownloadByUrl } from "~/API/Attachment";
import type { SupplierAttachmentView } from "~/Core/Models";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function formatDate(input: string | number): string {
  const date = new Date(input);
  return date.toLocaleDateString("en-US", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

export function absoluteUrl(path: string) {
  return `\\${path}`;
}

export function truncateText(text, maxLength = 50) {
  if ((text ?? "").length > maxLength) {
    return `${String(text)?.slice(0, maxLength)}...`;
  }
  return text as string;
}

const TIMEOUT = 5000;

export async function ExecuteWithTimeout(transactionCallback: Function) {
  let timeoutHandler: NodeJS.Timeout;

  const transactionPromise = new Promise<void>((resolve, reject) => {
    timeoutHandler = setTimeout(() => {
      reject(new Error("Transaction timed out due to a possible lock"));
    }, TIMEOUT);
    transactionCallback().then(resolve).catch(reject);
  });

  try {
    await transactionPromise;
  } catch (error) {
    console.error("Transaction failed or timed out:", error);
  } finally {
    clearTimeout(timeoutHandler);
  }
}

export function isEmpty(value) {
  if (!value) return true;
  if (Array.isArray(value)) {
    return value.length === 0;
  }
  if (value && typeof value === "object") {
    return Object.keys(value).length === 0;
  }
  return false;
}

export const downloadFile = async (file: SupplierAttachmentView | undefined) => {
  if (!file) return;
  try {
    const response = await Download({ filePath: file.id ?? 0 });

    // Create a blob link to download
    const blob = new Blob([response.data]);
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;

    // Optional: set filename if server doesn't set it
    link.setAttribute("download", file.originalFileName ?? "Attachment");

    document.body.appendChild(link);
    link.click();

    link.remove();
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error("Download error:", error);
  }
};

export const downloadFileByUrl = async (file: SupplierAttachmentView | PolicyReportAttachmentView | undefined) => {
  if (!file) return;
  try {
    const response = await DownloadByUrl({ filePath: file.id ?? 0 });

    // Create a blob link to download
    const blob = new Blob([response.data]);
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;

    const parts = file.id.split("/");
    const lastPart = parts[parts.length - 1]; // "20241107_MUSAHAMA%20S.P.%20Practitioners%20training_KnowledgeHubVersion.pdf"

    // لو حاب تفك تشفير الـ URL encoding
    const filename = decodeURIComponent(lastPart);

    // Optional: set filename if server doesn't set it
    link.setAttribute("download", filename ?? "Attachment");

    document.body.appendChild(link);
    link.click();

    link.remove();
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error("Download error:", error);
  }
};
